import React from 'react';
import { Tab } from 'react-bootstrap';
import { Route, Switch } from 'react-router-dom';
import RoutingTabs from '../RoutingTabs';
import styled from '@emotion/styled';
import { Alert, SpaceBetween } from '@amzn/awsui-components-react-v3/polaris';
import PricingContainer from '../../containers/Settings/PricingContainer';
import WeightingContainer from '../../containers/Settings/WeightingContainer';
import MediaTypeSubjectSettingsContainer from '../../containers/Settings/MediaTypeSubjectSettingsContainer';
import canDo from '../../utils/canDo';
import AutoReassignmentView from './AutoReassignment/AutoReassignmentView';
import BaseRatesView from './BaseRates/BaseRatesView';
import ChangeSetDetailView from './ChangeSets/ChangeSetDetail';
import { READ_ONLY_NOTE as CHANGE_SETS_READ_ONLY_NOTE } from './ChangeSets/changeSets';
import GoldenDatasetCreateView from './GoldenDatasets/GoldenDatasetCreate';
import GoldenDatasetDetailView from './GoldenDatasets/GoldenDatasetDetail';
import GoldenDatasetVersionView from './GoldenDatasets/GoldenDatasetVersion';
import GoldenDatasetsListView from './GoldenDatasets/GoldenDatasetsList';
import { READ_ONLY_NOTE as GOLDEN_DATASETS_READ_ONLY_NOTE } from './GoldenDatasets/goldenDatasets';
import {
  PricingModelCreateView,
  PricingModelEditView,
  PricingModelsListView,
} from './PricingModels/PricingModelsView';
import TerGroupCreateView from './TerGroups/TerGroupCreate';
import TerGroupDetailView from './TerGroups/TerGroupDetail';
import TerGroupsListView from './TerGroups/TerGroupsList';
import { READ_ONLY_NOTE as TER_GROUPS_READ_ONLY_NOTE } from './TerGroups/terGroups';
import ScopingModelCreateView from './ScopingModels/ScopingModelCreate';
import ScopingModelDetailView from './ScopingModels/ScopingModelDetail';
import ScopingModelsListView from './ScopingModels/ScopingModelsList';
import { READ_ONLY_NOTE as SCOPING_MODELS_READ_ONLY_NOTE } from './ScopingModels/scopingModels';

const Container = styled('div')`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  font-family: Arial, sans-serif;
`;

function Header() {
  return (
    <header id="header">
      <h1>Settings</h1>
    </header>
  );
}

interface PageAccess {
  read: string;
  write: string;
  noteId: string;
  note: string;
}

const TER_GROUP_ACCESS: PageAccess = {
  read: 'terGroup:read',
  write: 'terGroup:write',
  noteId: 'terGroupsReadOnly',
  note: TER_GROUPS_READ_ONLY_NOTE,
};

const SCOPING_MODEL_ACCESS: PageAccess = {
  read: 'scopingModel:read',
  write: 'scopingModel:write',
  noteId: 'scopingModelsReadOnly',
  note: SCOPING_MODELS_READ_ONLY_NOTE,
};

const GOLDEN_DATASET_ACCESS: PageAccess = {
  read: 'goldenDataset:read',
  write: 'goldenDataset:write',
  noteId: 'goldenDatasetsReadOnly',
  note: GOLDEN_DATASETS_READ_ONLY_NOTE,
};

// Change sets are reviewed by organization and system admins, the roles the portal web service
// gives the scoping-model permissions to.
const CHANGE_SET_ACCESS: PageAccess = {
  read: 'scopingModel:read',
  write: 'scopingModel:write',
  noteId: 'changeSetsReadOnly',
  note: CHANGE_SETS_READ_ONLY_NOTE,
};

// FIXME: use non-any types once they have been established up-stream
type SettingsProps = {
  session: any;
  features: any;
};

export class Settings extends React.Component<SettingsProps> {
  renderTabs = (): JSX.Element => {
    return (
      <RoutingTabs
        path="/settings"
        id="settingsTabs"
        fallback={canDo(this.props.session, 'terGroup:read') ? '/settings/terGroups' : undefined}
      >
        {canDo(this.props.session, 'admin:read') && (
          <Tab
            title="Media Types & Subjects"
            eventKey="mediaTypeSubject"
            mountOnEnter
            unmountOnExit
          >
            <MediaTypeSubjectSettingsContainer />
          </Tab>
        )}
        {canDo(this.props.session, 'admin:read') && (
          <Tab title="Pricing" eventKey="pricing" mountOnEnter unmountOnExit>
            <PricingContainer />
          </Tab>
        )}
        {canDo(this.props.session, 'admin:read') && (
          <Tab title="Weighting" eventKey="weighting" mountOnEnter unmountOnExit>
            <WeightingContainer />
          </Tab>
        )}
        {canDo(this.props.session, 'offerAutoReassignmentConfigs:read') && (
          <Tab title="Dynamic Assignment" eventKey="autoReassignment" mountOnEnter unmountOnExit>
            <AutoReassignmentView />
          </Tab>
        )}
      </RoutingTabs>
    );
  };

  renderAdminPage = (page: JSX.Element): JSX.Element =>
    canDo(this.props.session, 'admin:read') ? page : this.renderTabs();

  // Users who can read the page's records but not change them get a note above the page.
  renderGuardedPage = (access: PageAccess, page: JSX.Element): JSX.Element => {
    if (!canDo(this.props.session, access.read)) {
      return this.renderTabs();
    }
    if (this.canWrite(access)) {
      return page;
    }
    return (
      <SpaceBetween size="m">
        <Alert id={access.noteId} type="info">
          {access.note}
        </Alert>
        {page}
      </SpaceBetween>
    );
  };

  canWrite = (access: PageAccess): boolean => Boolean(canDo(this.props.session, access.write));

  render() {
    return (
      <Container>
        <Header />

        <Switch>
          <Route
            path="/settings/baseRate"
            render={(): JSX.Element =>
              canDo(this.props.session, 'admin:read') ? <BaseRatesView /> : this.renderTabs()
            }
          />
          <Route
            exact
            path="/settings/pricingModels"
            render={({ history }): JSX.Element =>
              this.renderAdminPage(
                <PricingModelsListView navigate={(path): void => history.push(path)} />
              )
            }
          />
          <Route
            exact
            path="/settings/pricingModels/new"
            render={({ history, location }): JSX.Element =>
              this.renderAdminPage(
                // Keyed on the visit so returning to this page always starts from an empty form.
                <PricingModelCreateView
                  key={location.key}
                  navigate={(path, state): void => history.push(path, state)}
                />
              )
            }
          />
          <Route
            exact
            path="/settings/pricingModels/:priceSchemeId"
            render={({ history, location, match }): JSX.Element =>
              this.renderAdminPage(
                <PricingModelEditView
                  priceSchemeId={match.params.priceSchemeId}
                  submittedChangeSetId={
                    location.state ? location.state.submittedChangeSetId : undefined
                  }
                  navigate={(path, state): void => history.push(path, state)}
                />
              )
            }
          />
          <Route
            exact
            path="/settings/terGroups"
            render={({ history }): JSX.Element =>
              this.renderGuardedPage(
                TER_GROUP_ACCESS,
                <TerGroupsListView
                  navigate={(path): void => history.push(path)}
                  canWrite={this.canWrite(TER_GROUP_ACCESS)}
                />
              )
            }
          />
          <Route
            exact
            path="/settings/terGroups/new"
            render={({ history, location }): JSX.Element =>
              this.renderGuardedPage(
                TER_GROUP_ACCESS,
                // Keyed on the visit so returning to this page always starts from an empty form.
                <TerGroupCreateView
                  key={location.key}
                  navigate={(path): void => history.push(path)}
                  canWrite={this.canWrite(TER_GROUP_ACCESS)}
                />
              )
            }
          />
          <Route
            exact
            path="/settings/terGroups/:terGroup"
            render={({ match }): JSX.Element =>
              this.renderGuardedPage(
                TER_GROUP_ACCESS,
                // Keyed on the group so moving between TER groups starts from a fresh page.
                <TerGroupDetailView
                  key={match.params.terGroup}
                  terGroup={match.params.terGroup}
                  canWrite={this.canWrite(TER_GROUP_ACCESS)}
                />
              )
            }
          />
          <Route
            exact
            path="/settings/scopingModels"
            render={({ history }): JSX.Element =>
              this.renderGuardedPage(
                SCOPING_MODEL_ACCESS,
                <ScopingModelsListView
                  navigate={(path): void => history.push(path)}
                  canWrite={this.canWrite(SCOPING_MODEL_ACCESS)}
                />
              )
            }
          />
          <Route
            exact
            path="/settings/scopingModels/new"
            render={({ history, location }): JSX.Element =>
              this.renderGuardedPage(
                SCOPING_MODEL_ACCESS,
                // Keyed on the visit so returning to this page always starts from an empty form.
                <ScopingModelCreateView
                  key={location.key}
                  navigate={(path, state): void => history.push(path, state)}
                  canWrite={this.canWrite(SCOPING_MODEL_ACCESS)}
                />
              )
            }
          />
          <Route
            exact
            path="/settings/scopingModels/:weightSchemeId"
            render={({ location, match }): JSX.Element =>
              this.renderGuardedPage(
                SCOPING_MODEL_ACCESS,
                // Keyed on the model so moving between scoping models starts from a fresh page.
                <ScopingModelDetailView
                  key={match.params.weightSchemeId}
                  weightSchemeId={match.params.weightSchemeId}
                  submittedChangeSetId={
                    location.state ? location.state.submittedChangeSetId : undefined
                  }
                  canWrite={this.canWrite(SCOPING_MODEL_ACCESS)}
                />
              )
            }
          />
          <Route
            exact
            path="/settings/goldenDatasets"
            render={({ history, location }): JSX.Element =>
              this.renderGuardedPage(
                GOLDEN_DATASET_ACCESS,
                <GoldenDatasetsListView
                  navigate={(path): void => history.push(path)}
                  canWrite={this.canWrite(GOLDEN_DATASET_ACCESS)}
                  deletedName={location.state ? location.state.deletedName : undefined}
                />
              )
            }
          />
          <Route
            exact
            path="/settings/goldenDatasets/new"
            render={({ history, location }): JSX.Element =>
              this.renderGuardedPage(
                GOLDEN_DATASET_ACCESS,
                // Keyed on the visit so returning to this page always starts from an empty form.
                <GoldenDatasetCreateView
                  key={location.key}
                  navigate={(path): void => history.push(path)}
                  canWrite={this.canWrite(GOLDEN_DATASET_ACCESS)}
                />
              )
            }
          />
          <Route
            exact
            path="/settings/goldenDatasets/:datasetId"
            render={({ history, match }): JSX.Element =>
              this.renderGuardedPage(
                GOLDEN_DATASET_ACCESS,
                // Keyed on the dataset so moving between datasets starts from a fresh page.
                <GoldenDatasetDetailView
                  key={match.params.datasetId}
                  datasetId={match.params.datasetId}
                  canWrite={this.canWrite(GOLDEN_DATASET_ACCESS)}
                  navigate={(path, state): void => history.push(path, state)}
                />
              )
            }
          />
          <Route
            exact
            path="/settings/goldenDatasets/:datasetId/version/:version"
            render={({ match }): JSX.Element =>
              this.renderGuardedPage(
                GOLDEN_DATASET_ACCESS,
                <GoldenDatasetVersionView
                  key={match.url}
                  datasetId={match.params.datasetId}
                  version={Number(match.params.version)}
                />
              )
            }
          />
          <Route
            exact
            path="/settings/changeSets/:changeSetId"
            render={({ match }): JSX.Element =>
              this.renderGuardedPage(
                CHANGE_SET_ACCESS,
                // Keyed on the change set so moving between change sets starts from a fresh page.
                <ChangeSetDetailView
                  key={match.params.changeSetId}
                  changeSetId={match.params.changeSetId}
                  sessionOrganizationId={this.props.session && this.props.session.organizationId}
                  canReview={this.canWrite(CHANGE_SET_ACCESS)}
                />
              )
            }
          />
          <Route render={this.renderTabs} />
        </Switch>
      </Container>
    );
  }
}
